import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LoginGateProvider } from "../context/login-gate";
import BottomTabBar from "./custom/bottom-tab-bar/bottom-tab-bar";
import ConfirmDialog from "./dialog/confirm/confirm-dialog";
import AuthService from "../service/auth-service";

//前台共用外框
//
//  本站是純手機版：整個 app 收在 .app-shell 這個固定寬度的置中欄位裡，
//  桌機看到的是同一份介面、兩側留白。因此沒有任何裝置判斷，也沒有第二套版型。
//
//  這裡刻意「不」放頂部列 —— 首頁用的是 NavList（logo + 分類），
//  次層頁面用 SubHeader（返回 + 標題），兩者結構差很多。
//  由各頁自己決定要哪一種，比在這裡寫一堆路由判斷清楚。
//
//  「登入已逾期」統一在這裡處理：任一支 API 回 401，index.js 的 axios 攔截器會
//  廣播 auth-expired 事件，這裡接住並跳一次 dialog。
const MainLayout = () => {
  const [showAuthExpired, setShowAuthExpired] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  //—— 底部頁籤只在首頁出現 ——
  //
  //  2026-10-28 改成白名單（Kevin）。原本是黑名單：除了
  //  /ask、/question、/purchase-result、/product/* 之外都顯示。
  //
  //  換成白名單的理由是「哪些頁面該有」比「哪些頁面不該有」好維護 ——
  //  黑名單每加一頁就要有人記得把它加進去，忘了就會在不該有頁籤的
  //  地方冒出來（而那種漏掉只有在真的點進去時才看得見）。
  //  實際上原本那張黑名單就已經漏過一次：/question 的結構跟 /ask
  //  一模一樣（底部固定一條輸入列），但它一直不在表上，頁籤會疊在輸入框上。
  //
  //  次層頁面都有 SubHeader 的返回鍵，不靠頁籤也離得開。
  const showTabBar = pathname === "/";

  //—— 沒有頁籤的頁面還要再分兩種 ——
  //
  //  沉浸式（產品頁的開場劇情、提問與問卦的滿版聊天、付款完的等待影片）
  //  底部 padding 必須是 0：那些頁面本身就是滿版高度，多一截 padding
  //  會把它撐出視窗，跑出一條什麼都沒有的捲軸。
  //
  //  一般次層頁（收藏庫、優惠券、評價、搜尋…）相反 —— 它們是會往下長的
  //  流式內容，padding 給 0 的話捲到底時最後一行會貼齊視窗底緣。
  //  實測 /reviews 在改成只有首頁顯示頁籤之後就是 0px。
  const IMMERSIVE = ["/ask", "/question", "/purchase-result"];
  const immersive =
    IMMERSIVE.includes(pathname) || pathname.startsWith("/product/");

  const shellMod = showTabBar
    ? ""
    : immersive
      ? " app-shell--bare"
      : " app-shell--sub";

  useEffect(() => {
    const onAuthExpired = () => setShowAuthExpired(true);
    window.addEventListener("auth-expired", onAuthExpired);
    return () => window.removeEventListener("auth-expired", onAuthExpired);
  }, []);

  const handleAuthExpiredConfirm = () => {
    AuthService.logout();
    setShowAuthExpired(false);
    navigate("/");
  };

  return (
    //LoginGateProvider 讓底下任何元件都能「在原地」要求登入 ——
    //未登入時不換頁，直接在目前畫面上跳彈窗（見 context/login-gate）
    <LoginGateProvider>
      {/* 沒有底部頁籤的頁面（沉浸式產品頁、提問頁）不需要為它預留空間 ——
          app-shell 那 96px 的 padding-bottom 會把內容撐出視窗，跑出一條捲軸。 */}
      <div className={`app-shell${shellMod}`}>
        {/* Outlet 會渲染對應路由的頁面元件（含該頁自己的頂部列） */}
        <Outlet />

        {/* 固定層：讓底部頁籤貼齊 app 欄位而不是視窗邊緣。
            桌機上兩側有留白，直接用 position: fixed 會讓它飛到畫面最外側。 */}
        <div className="app-fixed">
          {showTabBar && <BottomTabBar />}
        </div>

        <ConfirmDialog
          open={showAuthExpired}
          title="登入已逾期"
          message="為了保護您的帳號安全，登入狀態會定期到期，請重新登入以繼續使用。"
          confirmTxt="我知道了"
          onClose={handleAuthExpiredConfirm}
          onSuccess={handleAuthExpiredConfirm}
        />
      </div>
    </LoginGateProvider>
  );
};

export default MainLayout;
